import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart2, ChevronLeft, ChevronRight } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { getMemorizationProgress, formatChartDate } from '../../../services/analyticsService';

const PHASE_INTERVALS = { 0: 0, 1: 1, 2: 3, 3: 7, 4: 14, 5: 30, 6: 45, 7: 90 };
const CYCLE_INTERVAL = 10000; // 10 seconds

const AnalyticsPreviewCard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  // Data states
  const [progressData, setProgressData] = useState([]);
  const [progressLoading, setProgressLoading] = useState(true);
  const [scheduledData, setScheduledData] = useState([]);
  const [scheduledLoading, setScheduledLoading] = useState(true);
  const [calendarData, setCalendarData] = useState({});
  const [calendarLoading, setCalendarLoading] = useState(true);
  const [reviewHistoryData, setReviewHistoryData] = useState([]);
  const [reviewHistoryLoading, setReviewHistoryLoading] = useState(true);
  const [forecastData, setForecastData] = useState([]);
  const [forecastLoading, setForecastLoading] = useState(true);

  const graphs = [
    { id: 'memorization', title: 'Memorization Progress', loading: progressLoading },
    { id: 'scheduled', title: 'Scheduled Reviews', loading: scheduledLoading },
    { id: 'calendar', title: 'Calendar Review', loading: calendarLoading },
    { id: 'daily', title: 'Daily Review Activity', loading: reviewHistoryLoading },
    { id: 'forecast', title: '90 Day Forecast', loading: forecastLoading },
  ];

  // Auto-cycle
  useEffect(() => {
    if (isHovered) return;
    const timer = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % graphs?.length);
    }, CYCLE_INTERVAL);
    return () => clearInterval(timer);
  }, [isHovered, graphs?.length]);

  // Fetch memorization progress
  useEffect(() => {
    if (!user?.id) { setProgressLoading(false); return; }
    setProgressLoading(true);
    getMemorizationProgress(user?.id, '30days')?.then(data => setProgressData(data || []))?.catch(() => setProgressData([]))?.finally(() => setProgressLoading(false));
  }, [user?.id]);

  // Fetch scheduled reviews
  useEffect(() => {
    if (!user?.id) { setScheduledLoading(false); return; }
    setScheduledLoading(true);
    supabase?.from('verses')?.select('next_review_date')?.eq('user_id', user?.id)?.neq('current_phase', 0)?.not('next_review_date', 'is', null)?.then(({ data }) => {
        const dateMap = {};
        const today = new Date();
        today?.setHours(0, 0, 0, 0);
        (data || [])?.forEach(v => {
          if (v?.next_review_date) dateMap[v.next_review_date] = (dateMap?.[v?.next_review_date] || 0) + 1;
        });
        const arr = Object.entries(dateMap)?.map(([date, count]) => {
            const d = new Date(date);
            d?.setHours(0, 0, 0, 0);
            return {
              date, count,
              isOverdue: d < today,
              displayDate: new Date(date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
            };
          })?.sort((a, b) => new Date(a.date) - new Date(b.date))?.slice(0, 30);
        setScheduledData(arr);
      })?.catch(() => setScheduledData([]))?.finally(() => setScheduledLoading(false));
  }, [user?.id]);

  // Fetch calendar data (current year)
  useEffect(() => {
    if (!user?.id) { setCalendarLoading(false); return; }
    setCalendarLoading(true);
    const year = new Date()?.getFullYear();
    supabase?.from('user_daily_activity')?.select('activity_date, qualifies_for_streak')?.eq('user_id', user?.id)?.gte('activity_date', `${year}-01-01`)?.lte('activity_date', `${year}-12-31`)?.then(({ data }) => {
        const map = {};
        (data || [])?.forEach(a => { map[a.activity_date] = a?.qualifies_for_streak; });
        setCalendarData(map);
      })?.catch(() => setCalendarData({}))?.finally(() => setCalendarLoading(false));
  }, [user?.id]);

  // Fetch daily review activity
  useEffect(() => {
    if (!user?.id) { setReviewHistoryLoading(false); return; }
    setReviewHistoryLoading(true);
    supabase?.from('user_daily_activity')?.select('activity_date, reviews_completed, verses_practiced')?.eq('user_id', user?.id)?.order('activity_date', { ascending: true })?.limit(30)?.then(({ data }) => {
        setReviewHistoryData((data || [])?.map(a => ({
          date: new Date(a.activity_date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          reviews: a?.reviews_completed || 0,
          practiced: a?.verses_practiced || 0,
        })));
      })?.catch(() => setReviewHistoryData([]))?.finally(() => setReviewHistoryLoading(false));
  }, [user?.id]);

  // Fetch 90-day forecast
  useEffect(() => {
    if (!user?.id) { setForecastLoading(false); return; }
    setForecastLoading(true);
    supabase?.from('verses')?.select('current_phase, next_review_date, review_count, successful_review_count, verse_count')?.eq('user_id', user?.id)?.neq('current_phase', 0)?.then(({ data: verses }) => {
        if (!verses || verses?.length === 0) { setForecastLoading(false); return; }
        const totalReviewed = verses?.reduce((s, v) => s + (v?.review_count || 0), 0);
        const totalSuccessful = verses?.reduce((s, v) => s + (v?.successful_review_count || 0), 0);
        const successRate = totalReviewed >= 10 ? Math.min(totalSuccessful / totalReviewed, 1) : 0.9;
        const today = new Date();
        today?.setHours(0, 0, 0, 0);
        const dailyCounts = {};
        for (let i = 0; i < 90; i++) {
          const d = new Date(today);
          d?.setDate(d?.getDate() + i);
          dailyCounts[d.toISOString().split('T')[0]] = 0;
        }
        verses?.forEach(verse => {
          let nextReview = verse?.next_review_date ? new Date(verse.next_review_date) : new Date(today);
          nextReview?.setHours(0, 0, 0, 0);
          let phase = verse?.current_phase || 1;
          const weight = verse?.verse_count || 1;
          for (let attempt = 0; attempt < 30; attempt++) {
            const dateStr = nextReview?.toISOString()?.split('T')?.[0];
            const diff = Math.floor((nextReview - today) / 86400000);
            if (diff >= 90) break;
            if (dailyCounts?.hasOwnProperty(dateStr)) dailyCounts[dateStr] += weight;
            if (successRate >= 0.5 && phase < 7) phase = Math.min(phase + 1, 7);
            else if (successRate < 0.5 && phase > 1) phase = Math.max(phase - 1, 1);
            const interval = Math.max(PHASE_INTERVALS?.[phase] || 1, 1);
            nextReview = new Date(nextReview);
            nextReview?.setDate(nextReview?.getDate() + interval);
          }
        });
        const arr = Object.entries(dailyCounts)?.map(([date, count]) => {
            const d = new Date(date);
            const day = Math.floor((d - today) / 86400000);
            return { day, count, displayDate: d?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) };
          })?.sort((a, b) => a?.day - b?.day);
        setForecastData(arr);
      })?.catch(() => setForecastData([]))?.finally(() => setForecastLoading(false));
  }, [user?.id]);

  const goTo = (idx) => setCurrentIndex(((idx % graphs?.length) + graphs?.length) % graphs?.length);

  const MiniCalendar = () => {
    const year = new Date()?.getFullYear();
    const grid = Array.from({ length: 7 }, () => []);
    const startDate = new Date(year, 0, 1);
    const endDate = new Date(year, 11, 31);
    const firstDay = startDate?.getDay();
    for (let r = 0; r < firstDay; r++) grid?.[r]?.push(null);
    let row = firstDay;
    for (let d = new Date(startDate); d <= endDate; d?.setDate(d?.getDate() + 1)) {
      const ds = d?.toISOString()?.split('T')?.[0];
      grid?.[row]?.push({ date: new Date(d), completed: calendarData?.[ds], dateStr: ds });
      row++;
      if (row === 7) row = 0;
    }
    const maxLen = Math.max(...grid?.map(r => r?.length));
    grid?.forEach(r => { while (r?.length < maxLen) r?.push(null); });
    return (
      <div className="overflow-x-auto">
        <div className="flex gap-1">
          {grid?.[0]?.map((_, wi) => (
            <div key={wi} className="flex flex-col gap-0.5">
              {grid?.map((r, di) => {
                const day = r?.[wi];
                return (
                  <div key={di}
                    className={`w-2.5 h-2.5 rounded-sm ${day === null ? 'bg-transparent' : day?.completed ? 'bg-success' : 'bg-muted'}`}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderChart = () => {
    const current = graphs?.[currentIndex];
    if (current?.loading) {
      return (
        <div className="flex items-center justify-center h-[180px]">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
        </div>
      );
    }

    if (current?.id === 'memorization') {
      if (!progressData?.length) return <div className="flex items-center justify-center h-[180px] text-sm text-muted-foreground">No data yet</div>;
      return (
        <ResponsiveContainer width="100%" height={180}>
          <LineChart data={progressData}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis dataKey="date" tickFormatter={d => formatChartDate(d, '30days')} angle={-45} textAnchor="end" height={50} tick={{ fill: 'var(--color-muted-foreground)', fontSize: 10 }} />
            <YAxis tick={{ fill: 'var(--color-muted-foreground)', fontSize: 10 }} />
            <Tooltip contentStyle={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', color: 'var(--color-foreground)', fontSize: 11 }} />
            <Line type="monotone" dataKey="total" stroke="var(--color-primary)" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="phase7" stroke="var(--color-success)" strokeWidth={1.5} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      );
    }

    if (current?.id === 'scheduled') {
      if (!scheduledData?.length) return <div className="flex items-center justify-center h-[180px] text-sm text-muted-foreground">No scheduled reviews</div>;
      return (
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={scheduledData}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis dataKey="displayDate" angle={-45} textAnchor="end" height={50} interval={Math.floor(scheduledData?.length / 8) || 0} tick={{ fill: 'var(--color-muted-foreground)', fontSize: 10 }} />
            <YAxis tick={{ fill: 'var(--color-muted-foreground)', fontSize: 10 }} />
            <Tooltip contentStyle={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', color: 'var(--color-foreground)', fontSize: 11 }} />
            <Bar dataKey="count" shape={(props) => {
              const { x, y, width, height, payload } = props;
              return <rect x={x} y={y} width={width} height={height} fill={payload?.isOverdue ? 'var(--color-destructive)' : 'var(--color-primary)'} />;
            }} />
          </BarChart>
        </ResponsiveContainer>
      );
    }

    if (current?.id === 'calendar') {
      return (
        <div className="px-2 py-3">
          <MiniCalendar />
          <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground">
            <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 bg-success rounded-sm"></div><span>Completed</span></div>
            <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 bg-muted rounded-sm"></div><span>Missed</span></div>
          </div>
        </div>
      );
    }

    if (current?.id === 'daily') {
      if (!reviewHistoryData?.length) return <div className="flex items-center justify-center h-[180px] text-sm text-muted-foreground">No activity yet</div>;
      return (
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={reviewHistoryData}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis dataKey="date" angle={-45} textAnchor="end" height={50} interval={Math.floor(reviewHistoryData?.length / 8) || 0} tick={{ fill: 'var(--color-muted-foreground)', fontSize: 10 }} />
            <YAxis tick={{ fill: 'var(--color-muted-foreground)', fontSize: 10 }} />
            <Tooltip contentStyle={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', color: 'var(--color-foreground)', fontSize: 11 }} />
            <Bar dataKey="reviews" fill="var(--color-primary)" radius={[2, 2, 0, 0]} />
            <Bar dataKey="practiced" fill="var(--color-success)" radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      );
    }

    if (current?.id === 'forecast') {
      if (!forecastData?.length) return <div className="flex items-center justify-center h-[180px] text-sm text-muted-foreground">No forecast data</div>;
      return (
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={forecastData}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis dataKey="displayDate" angle={-45} textAnchor="end" height={50} interval={Math.floor(forecastData?.length / 8) || 0} tick={{ fill: 'var(--color-muted-foreground)', fontSize: 10 }} />
            <YAxis tick={{ fill: 'var(--color-muted-foreground)', fontSize: 10 }} />
            <Tooltip contentStyle={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)', color: 'var(--color-foreground)', fontSize: 11 }} />
            <Bar dataKey="count" fill="var(--color-accent)" radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      );
    }

    return null;
  };

  return (
    <div
      className="bg-card border border-border rounded-lg overflow-hidden cursor-pointer hover:border-primary/50 transition-colors mb-4"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => navigate('/study-analytics-dashboard')}
      title="Click to view full analytics"
    >
      {/* Card Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
        <div className="flex items-center gap-2">
          <BarChart2 size={16} className="text-primary" />
          <span className="text-sm font-semibold text-foreground">{graphs?.[currentIndex]?.title}</span>
        </div>
        <div className="flex items-center gap-2">
          {/* Dot indicators */}
          <div className="flex items-center gap-1">
            {graphs?.map((_, i) => (
              <button
                key={i}
                onClick={(e) => { e?.stopPropagation(); goTo(i); }}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${i === currentIndex ? 'bg-primary' : 'bg-muted-foreground/30'}`}
              />
            ))}
          </div>
          {/* Prev/Next */}
          <button
            onClick={(e) => { e?.stopPropagation(); goTo(currentIndex - 1); }}
            className="p-0.5 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft size={14} />
          </button>
          <button
            onClick={(e) => { e?.stopPropagation(); goTo(currentIndex + 1); }}
            className="p-0.5 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
          >
            <ChevronRight size={14} />
          </button>
          <span className="text-xs text-muted-foreground ml-1">View All →</span>
        </div>
      </div>
      {/* Chart Area */}
      <div className="px-2 pt-2 pb-1">
        {renderChart()}
      </div>
    </div>
  );
};

export default AnalyticsPreviewCard;
